import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, LogOut, RefreshCw, Phone, Mail, Inbox } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { BUSINESS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "scheduled", "completed"];

const STATUS_STYLES = {
  new: "bg-fc-orange/10 text-fc-orange border-fc-orange/30",
  contacted: "bg-fc-sky/10 text-fc-cobalt border-fc-sky/30",
  scheduled: "bg-fc-navy/10 text-fc-navy border-fc-navy/30",
  completed: "bg-green-600/10 text-green-700 border-green-600/30",
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-fc-ink/20 py-3 text-fc-ink placeholder:text-fc-ink2/50 focus:outline-none focus:border-fc-cobalt transition-colors";

const LoginCard = () => {
  const { login } = useAuth();
  const [mode, setMode] = useState("login"); // login | forgot | forgot-sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
      } else {
        await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
        setMode("forgot-sent");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen surface-light flex items-center justify-center px-5">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md bg-white border border-fc-ink/10 shadow-[0_30px_70px_rgba(11,44,92,0.12)] p-8 sm:p-10">
        <div className="inline-block bg-white rounded-lg border border-fc-ink/10 p-2 mb-6">
          <img src="/fc-logo.png" alt="F&C Air Conditioning" className="h-10 w-auto" />
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-fc-ink">
          {mode === "login" ? "Admin Sign In" : "Reset Password"}
        </h1>
        <p className="mt-2 text-sm text-fc-ink2">
          {mode === "login" && "View and manage service requests."}
          {mode === "forgot" && "Enter your admin email and we'll send you a reset link."}
          {mode === "forgot-sent" && "If an account exists for that email, a reset link is on its way. Check your inbox."}
        </p>
        {mode !== "forgot-sent" && (
          <div className="mt-8 space-y-6">
            <div>
              <label htmlFor="admin-email" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Email</label>
              <input id="admin-email" data-testid="admin-email-input" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
            </div>
            {mode === "login" && (
              <div>
                <label htmlFor="admin-password" className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2">Password</label>
                <input id="admin-password" data-testid="admin-password-input" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
            )}
          </div>
        )}
        {error && <div data-testid="admin-login-error" className="mt-5 text-sm font-semibold text-red-600">{error}</div>}
        {mode !== "forgot-sent" && (
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-fc-orange text-white py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all disabled:opacity-60"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Please wait…</> : mode === "login" ? "Sign In" : "Send Reset Link"}
          </button>
        )}
        <div className="mt-5 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest">
          <Link to="/" data-testid="admin-back-home" className="text-fc-ink2 hover:text-fc-ink">← Website</Link>
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => { setError(""); setMode(mode === "login" ? "forgot" : "login"); }}
            className="text-fc-cobalt hover:text-fc-navy"
          >
            {mode === "login" ? "Forgot password?" : "Back to sign in"}
          </button>
        </div>
      </form>
    </div>
  );
};

const LeadCard = ({ lead, onStatus }) => (
  <div data-testid={`lead-card-${lead.id}`} className="bg-white border border-fc-ink/10 p-5 sm:p-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="font-display text-xl font-semibold tracking-tight text-fc-ink">
          {lead.first_name} {lead.last_name}
        </div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-fc-ink2">
          {new Date(lead.created_at).toLocaleString()} · {lead.property_type}
        </div>
      </div>
      <select
        data-testid={`lead-status-select-${lead.id}`}
        value={lead.status}
        onChange={(e) => onStatus(lead.id, e.target.value)}
        className={`border px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest font-bold ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}
      >
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-fc-ink2">
      <a href={`tel:${lead.phone}`} data-testid={`lead-phone-${lead.id}`} className="inline-flex items-center gap-2 hover:text-fc-ink"><Phone size={14} className="text-fc-orange" /> {lead.phone}</a>
      <a href={`mailto:${lead.email}`} data-testid={`lead-email-${lead.id}`} className="inline-flex items-center gap-2 hover:text-fc-ink break-all"><Mail size={14} className="text-fc-orange" /> {lead.email}</a>
    </div>
    <div className="mt-3 text-sm"><span className="font-bold text-fc-ink">Service:</span> <span className="text-fc-ink2">{lead.service_needed}</span></div>
    {lead.message && <p className="mt-2 text-sm text-fc-ink2 border-l-2 border-fc-orange/40 pl-3">{lead.message}</p>}
  </div>
);

const Dashboard = () => {
  const { user, logout, authHeaders } = useAuth();
  const [leads, setLeads] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/service-requests`, { headers: authHeaders(), withCredentials: true });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setLeads([]);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/service-requests/${id}`, { status }, { headers: authHeaders(), withCredentials: true });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const newCount = (leads || []).filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen surface-light">
      <header className="bg-fc-navy text-white">
        <div className="mx-auto w-[92%] max-w-[1280px] py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-lg p-1.5"><img src="/fc-logo.png" alt="F&C Air Conditioning" className="h-8 w-auto" /></div>
            <div>
              <div className="font-display font-bold tracking-tight leading-none">Service Requests</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/60 mt-1">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={load} className="inline-flex items-center gap-2 border border-white/30 hover:border-white px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors">
              <RefreshCw size={14} /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 bg-fc-orange px-4 py-2 font-mono text-[11px] uppercase tracking-widest hover:brightness-95 transition-all">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto w-[92%] max-w-[1280px] py-10">
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div data-testid="stat-total" className="bg-white border border-fc-ink/10 p-5">
            <div className="font-display text-4xl font-bold text-fc-navy">{leads ? leads.length : "—"}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2 mt-1">Total Requests</div>
          </div>
          <div data-testid="stat-new" className="bg-white border border-fc-ink/10 p-5">
            <div className="font-display text-4xl font-bold text-fc-orange">{leads ? newCount : "—"}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-fc-ink2 mt-1">New</div>
          </div>
        </div>

        <div data-testid="leads-list" className="mt-8 space-y-4">
          {leads === null && (
            <div className="flex items-center gap-3 text-fc-ink2 py-16 justify-center"><Loader2 size={20} className="animate-spin" /> Loading requests…</div>
          )}
          {leads !== null && leads.length === 0 && (
            <div data-testid="leads-empty" className="flex flex-col items-center gap-3 text-fc-ink2 py-16">
              <Inbox size={32} className="text-fc-sky" /> No service requests yet.
            </div>
          )}
          {(leads || []).map((lead) => <LeadCard key={lead.id} lead={lead} onStatus={onStatus} />)}
        </div>
      </main>
    </div>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen surface-light flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-fc-navy" data-testid="admin-loading" />
      </div>
    );
  }
  return user ? <Dashboard /> : <LoginCard />;
}
